import { type Dispatch, type SetStateAction, useState } from "react";
import type { GraphResponse, GraphNode, Persona } from "../aidwops.types";
import tooltipsData from "./AidwopsTooltips.json";

const LEVEL_BY_TYPE: Record<GraphNode["type"], number> = {
  orchestrator: 0,
  agent: 1,
  tool: 2,
  source: 3,
};

const NODE_COLOR: Record<GraphNode["status"], { fill: string; stroke: string }> = {
  healthy: { fill: "var(--success-tint)", stroke: "var(--success)" },
  degraded: { fill: "var(--eon-red-tint)", stroke: "var(--eon-red)" },
  unknown: { fill: "var(--surface)", stroke: "var(--faint)" },
};

function layout(graph: GraphResponse, width: number) {
  const byLevel: Record<number, GraphNode[]> = {};
  for (const node of graph.nodes) {
    const level = LEVEL_BY_TYPE[node.type];
    (byLevel[level] ??= []).push(node);
  }
  const positions = new Map<string, { x: number; y: number }>();
  const levelHeight = width < 500 ? 60 : 80;
  Object.entries(byLevel).forEach(([levelStr, nodes]) => {
    const level = Number(levelStr);
    const gap = width / (nodes.length + 1);
    nodes.forEach((node, i) => {
      positions.set(node.id, { x: gap * (i + 1), y: level * levelHeight + 30 });
    });
  });
  return { positions, levelHeight };
}

function GraphSvg({ graph, width, nodeR }: { graph: GraphResponse; width: number; nodeR: number }) {
  const { positions, levelHeight } = layout(graph, width);
  const maxLevel = Math.max(...graph.nodes.map((n) => LEVEL_BY_TYPE[n.type]), 0);
  const height = (maxLevel + 1) * levelHeight;
  const fontSize = width < 500 ? 7 : 9.5;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} role="img" aria-label="Service dependency graph">
      <g stroke="var(--border)" strokeWidth={1.6} fill="none">
        {graph.edges.map((edge) => {
          const from = positions.get(edge.from);
          const to = positions.get(edge.to);
          if (!from || !to) return null;
          return <path key={`${edge.from}-${edge.to}`} d={`M${from.x} ${from.y} L${to.x} ${to.y}`} />;
        })}
      </g>
      {graph.nodes.map((node) => {
        const pos = positions.get(node.id);
        if (!pos) return null;
        const level = LEVEL_BY_TYPE[node.type];
        const r = nodeR - level * (nodeR > 16 ? 3 : 1.5);
        const color = NODE_COLOR[node.status];
        return (
          <g key={node.id}>
            <circle cx={pos.x} cy={pos.y} r={r} fill={color.fill} stroke={color.stroke} strokeWidth={2} />
            <text x={pos.x} y={pos.y + fontSize / 3} textAnchor="middle" fontSize={fontSize} fontWeight={700} fill={color.stroke}>
              {node.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

const LEGEND = [
  { color: "var(--success)", label: "Healthy" },
  { color: "var(--eon-red)", label: "Needs attention" },
  { color: "var(--faint)", label: "Data source" },
];

export function ServiceGraphMini({ graph, loading }: { graph: GraphResponse; loading?: boolean }) {
  const tooltip = tooltipsData?.service_graph;
  return (
    <div className="card graph-card" title={tooltip}>
      <div className="card-header">
        <span className="ic">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
            <circle cx="6" cy="6" r="2.2" stroke="currentColor" strokeWidth="1.7" />
            <circle cx="18" cy="6" r="2.2" stroke="currentColor" strokeWidth="1.7" />
            <circle cx="12" cy="18" r="2.2" stroke="currentColor" strokeWidth="1.7" />
            <path d="m8 7 2 8M16 7l-2 8M8.3 6h7.4" stroke="currentColor" strokeWidth="1.5" />
          </svg>
        </span>
        <div>
          <div className="card-title">Service graph</div>
          <div className="card-title-sub">Live agent → tool → source topology</div>
        </div>
      </div>
      {loading && graph.nodes.length === 0 ? (
        <div className="skeleton" style={{ height: 200, borderRadius: 10 }} />
      ) : (
        <div className="graph-canvas-wrap">
          <GraphSvg graph={graph} width={380} nodeR={16} />
        </div>
      )}
      <div className="graph-legend">
        {LEGEND.map((l) => (
          <span key={l.label} className="legend-item">
            <span className="legend-dot" style={{ background: l.color }} />
            {l.label}
          </span>
        ))}
      </div>
    </div>
  );
}

export function ServiceGraphFull({
  graph,
  filter,
  onFilterChange,
  loading,
}: {
  graph: GraphResponse;
  filter: Persona | "degraded";
  // eslint-disable-next-line no-unused-vars
  onFilterChange: Dispatch<SetStateAction<Persona | "degraded">>;
  loading?: boolean;
}) {
  const tooltip = tooltipsData?.service_graph;
  return (
    <section className="card service-graph-wrap" title={tooltip}>
      <div className="card-header" style={{ justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span className="ic">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
              <circle cx="6" cy="6" r="2.2" stroke="currentColor" strokeWidth="1.7" />
              <circle cx="18" cy="6" r="2.2" stroke="currentColor" strokeWidth="1.7" />
              <circle cx="12" cy="18" r="2.2" stroke="currentColor" strokeWidth="1.7" />
              <path d="m8 7 2 8M16 7l-2 8M8.3 6h7.4" stroke="currentColor" strokeWidth="1.5" />
            </svg>
          </span>
          <div>
            <div className="card-title">Full IT service graph</div>
            <div className="card-title-sub">
              Live agent/tool/source topology — health overlaid from the same insight data (Neo4j-backed once provisioned)
            </div>
          </div>
        </div>
        <div className="graph-toolbar">
          {(["all", "zscaler", "intune", "degraded"] as const).map((f) => (
            <button
              key={f}
              className={`graph-filter${filter === f ? " active" : ""}`}
              onClick={() => onFilterChange(f)}
            >
              {f === "all" ? "All" : f === "degraded" ? "Degraded only" : f[0].toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>
      {loading && graph.nodes.length === 0 ? (
        <div className="skeleton" style={{ height: 300, borderRadius: 10 }} />
      ) : (
        <GraphSvg graph={graph} width={1000} nodeR={22} />
      )}
      <div className="graph-legend">
        {LEGEND.map((l) => (
          <span key={l.label} className="legend-item">
            <span className="legend-dot" style={{ background: l.color }} />
            {l.label}
          </span>
        ))}
      </div>
    </section>
  );
}

export function ServiceGraph({ graph }: { graph: GraphResponse }) {
  const [filter, setFilter] = useState<Persona | "degraded">("all");
  return <ServiceGraphFull graph={graph} filter={filter} onFilterChange={setFilter} />;
}
